import React, { useEffect, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    CircularProgress,
    TablePagination,
    TableSortLabel,
    useTheme,
    useMediaQuery,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Popper,
    Fade,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Paper,
    Tooltip,
    Rating
} from '@mui/material';
import { Link, Visibility } from '@mui/icons-material';
import { BasicButton } from '../../../../../components/BasicButton';
import { UserActivityWithDetails } from '../../../../../services/analytics/AnalyticsService';
import { ActivityTypeColors } from '../../../../../config/constants';
import { TableControlBar } from '../../../../../components/TableControlBar';
import { PortalTable } from '../../../../../components/PortalTable';
import { tablePreferencesStore } from '../../../../../app/stores/TablePreferences';
import { AuditFilters, AuditFilterConfig } from '../../../../../components/AuditFilters';

interface ActivityTableProps {
    activities: UserActivityWithDetails[];
    loading: boolean;
    onRefresh?: () => void;
    searchTerm?: string;
    onSearchChange?: (value: string) => void;
    filters: AuditFilterConfig;
    onFiltersChange: (filters: AuditFilterConfig) => void;
    availableActivityTypes: string[];
    availableDeviceTypes: string[];
    availableScreens: string[];
}

const AuditTable: React.FC<ActivityTableProps> = ({
    activities,
    loading,
    onRefresh,
    searchTerm = '',
    onSearchChange,
    filters,
    onFiltersChange,
    availableActivityTypes,
    availableDeviceTypes,
    availableScreens
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Get preferences from store
    const { 
        audit: auditPrefs, 
        updateAuditPreferences 
    } = tablePreferencesStore();
    
    // Use store values with local state for page and search (not persisted)
    const [order, setOrder] = useState<'asc' | 'desc'>(auditPrefs.sortOrder);
    const [orderBy, setOrderBy] = useState<keyof UserActivityWithDetails>(auditPrefs.sortBy as keyof UserActivityWithDetails);
    const [page, setPage] = useState(0); // Session-only
    const [rowsPerPage, setRowsPerPage] = useState(auditPrefs.rowsPerPage);
    const [dense, setDense] = useState(auditPrefs.dense);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedActivityData, setSelectedActivityData] = useState<Record<string, any> | null>(null);
    const [selectedActivityType, setSelectedActivityType] = useState<string | null>(null);
    const [linkedUsersAnchor, setLinkedUsersAnchor] = useState<null | HTMLElement>(null);
    const [selectedLinkedUsers, setSelectedLinkedUsers] = useState<any[]>([]);

    // Reset page to 0 when filters change
    useEffect(() => {
        setPage(0);
    }, [filters]);

    const handleOpenDetails = (activityData: Record<string, any>, activityType?: string) => {
        setSelectedActivityData(activityData);
        setSelectedActivityType(activityType || null);
        setDetailsModalOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailsModalOpen(false);
        setSelectedActivityData(null);
        setSelectedActivityType(null);
    };

    const handleLinkedUsersClick = (event: React.MouseEvent<HTMLElement>, linkedUsers: any[]) => {
        setLinkedUsersAnchor(linkedUsersAnchor ? null : event.currentTarget);
        setSelectedLinkedUsers(linkedUsers);
    };

    const handleCloseLinkedUsers = () => {
        setLinkedUsersAnchor(null);
    };

    const linkedUsersOpen = Boolean(linkedUsersAnchor);

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - activities.length) : 0;

    const sortedActivities = activities.slice().sort((a, b) => {
        if (a[orderBy] === null && b[orderBy] === null) return 0;
        if (a[orderBy] === null) return 1;
        if (b[orderBy] === null) return -1;

        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }

        return 0;
    });

    const handleRequestSort = (_event: React.MouseEvent<unknown>, property: keyof UserActivityWithDetails) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);
        
        // Persist to store
        updateAuditPreferences({
            sortOrder: newOrder,
            sortBy: property as string
        });
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = +event.target.value;
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        
        // Persist to store
        updateAuditPreferences({ rowsPerPage: newRowsPerPage });
    };

    const handleRefresh = async () => {
        if (onRefresh) {
            setIsRefreshing(true);
            await onRefresh();
            setIsRefreshing(false);
        }
    };

    // Generate consistent, distinct color for anonymous ID
    const getAnonymousColor = (anonymousId: string): string => {
        // Predefined vibrant, distinct colors
        const colors = [
            '#FF6B6B', // Red
            '#4ECDC4', // Teal
            '#45B7D1', // Blue
            '#FFA07A', // Salmon
            '#98D8C8', // Mint
            '#F7DC6F', // Yellow
            '#BB8FCE', // Purple
            '#85C1E2', // Sky Blue
            '#F8B88B', // Peach
            '#ABEBC6', // Light Green
            '#FAD7A0', // Light Orange
            '#D7BDE2', // Lavender
            '#A3E4D7', // Aqua
            '#F9E79F', // Light Yellow
            '#F5B7B1', // Pink
            '#AED6F1', // Powder Blue
            '#A9DFBF', // Sage
            '#F5CBA7', // Tan
            '#D2B4DE', // Mauve
            '#A2D9CE', // Seafoam
        ];

        // Simple hash to consistently pick a color
        let hash = 0;
        for (let i = 0; i < anonymousId.length; i++) {
            hash = anonymousId.charCodeAt(i) + ((hash << 5) - hash);
        }

        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    const getActivityTypeColor = (type: string | undefined): string => {
        if (!type) return '#9E9E9E'; // Grey
        
        // Use centralized color mapping from constants
        return ActivityTypeColors[type as keyof typeof ActivityTypeColors] || '#9E9E9E';
    };

    const formatActivityType = (type: string | undefined): string => {
        if (!type) return '-';

        return type
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);

        return date.toLocaleString('en-ZA', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    if (loading && activities.length === 0) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' minHeight={300}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                mb: 4,
            }}
        >
            {/* Controls Bar with Search */}
            <TableControlBar
                dense={dense}
                onDenseChange={(newDense) => {
                    setDense(newDense);
                    updateAuditPreferences({ dense: newDense });
                }}
                isLoading={isRefreshing}
                onRefresh={onRefresh ? handleRefresh : undefined}
                showRefresh={!!onRefresh}
                search={onSearchChange ? {
                    value: searchTerm,
                    onChange: onSearchChange,
                    placeholder: 'Search all fields...'
                } : undefined}
                filterDescription={
                    searchTerm
                        ? `Showing ${activities.length} ${activities.length === 1 ? 'activity' : 'activities'} matching "${searchTerm}"`
                        : `Showing all ${activities.length} ${activities.length === 1 ? 'activity' : 'activities'}`
                }
                customActions={
                    <AuditFilters
                        filters={filters}
                        onChange={onFiltersChange}
                        availableActivityTypes={availableActivityTypes}
                        availableDeviceTypes={availableDeviceTypes}
                        availableScreens={availableScreens}
                    />
                }
            />

            <PortalTable>
                <TableContainer>
                    <Table stickyHeader size={dense ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ px: isMobile ? 1 : 2, minWidth: isMobile ? 70 : 100 }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 0.5, fontSize: isMobile ? '0.75rem' : undefined }}
                                        active={orderBy === 'created'}
                                        direction={orderBy === 'created' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'created')}
                                    >
                                        Time
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ px: isMobile ? 1 : 2 }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 0.5, fontSize: isMobile ? '0.75rem' : undefined }}
                                        active={orderBy === 'anonymousId'}
                                        direction={orderBy === 'anonymousId' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'anonymousId')}
                                    >
                                        {isMobile ? 'User' : 'Anonymous ID'}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ px: isMobile ? 1 : 2 }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 0.5, fontSize: isMobile ? '0.75rem' : undefined }}
                                        active={orderBy === 'activityType'}
                                        direction={orderBy === 'activityType' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'activityType')}
                                    >
                                        Activity
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, px: 2 }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 0.5 }}
                                        active={orderBy === 'screenName'}
                                        direction={orderBy === 'screenName' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'screenName')}
                                    >
                                        Screen
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 0.5 }}
                                        active={orderBy === 'deviceType'}
                                        direction={orderBy === 'deviceType' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'deviceType')}
                                    >
                                        Device
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                    <Typography
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 0.5 }}
                                    >
                                        Details
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedActivities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((activity) => {
                                return (
                                <TableRow key={activity.id} hover>
                                        {/* Time Cell - stacked on mobile */}
                                        <TableCell sx={{
                                            px: isMobile ? 1.5 : 2,
                                            py: isMobile ? 1.5 : undefined,
                                            verticalAlign: isMobile ? 'middle' : 'top',
                                            minWidth: isMobile ? 55 : 100
                                        }}>
                                            {isMobile ? (
                                                <Box display='flex' flexDirection='column' alignItems='flex-start'>
                                                    <Typography variant='caption' sx={{ fontSize: '0.7rem', fontWeight: 600, lineHeight: 1.3 }}>
                                                        {new Date(activity.created).toLocaleString('en-ZA', { day: 'numeric', month: 'short' })}
                                                    </Typography>
                                                    <Typography variant='caption' sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.3 }}>
                                                        {new Date(activity.created).toLocaleString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant='caption' sx={{ fontSize: '0.75rem' }}>
                                                    {formatDate(activity.created)}
                                                </Typography>
                                            )}
                                    </TableCell>

                                        {/* User Cell - stacked on mobile with ID visible */}
                                        <TableCell sx={{
                                            px: isMobile ? 1 : 2,
                                            py: isMobile ? 1.5 : undefined,
                                            verticalAlign: isMobile ? 'middle' : 'top'
                                        }}>
                                        {activity.anonymousId ? (
                                                <Box display='flex' alignItems='center' gap={isMobile ? 1 : 1.5}>
                                                    <Tooltip title={activity.anonymousId.toUpperCase()} arrow>
                                                        <Avatar
                                                            sx={{
                                                                width: isMobile ? 32 : 32,
                                                                height: isMobile ? 32 : 32,
                                                                bgcolor: getAnonymousColor(activity.anonymousId),
                                                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                                                fontWeight: 'bold',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            {activity.anonymousId.toUpperCase().slice(0, 2)}
                                                        </Avatar>
                                                    </Tooltip>
                                                    <Box display='flex' flexDirection='column' flex={1} minWidth={0} gap={0.5}>
                                                        <Typography
                                                            variant='subtitle2'
                                                            fontWeight={600}
                                                            sx={{
                                                                fontSize: isMobile ? '0.75rem' : '0.8rem',
                                                                lineHeight: 1.2
                                                            }}
                                                        >
                                                            {activity.anonymousId.slice(0, 8).toUpperCase()}
                                                    </Typography>
                                                        {activity.linkedUsers && activity.linkedUsers.length > 0 ? (
                                                            <Box
                                                                display='flex'
                                                                alignItems='center'
                                                                gap={0.5}
                                                                onClick={(e) => handleLinkedUsersClick(e as any, activity.linkedUsers || [])}
                                                                sx={{ cursor: 'pointer' }}
                                                            >
                                                                <Link sx={{ fontSize: isMobile ? 12 : 14, color: 'success.main' }} />
                                                                <Typography variant='caption' color='success.main' sx={{ fontSize: isMobile ? '0.6rem' : '0.65rem', lineHeight: 1.2 }}>
                                                                    {activity.linkedUsers.length} linked
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                                <Typography variant='caption' color='text.disabled' sx={{ fontSize: isMobile ? '0.6rem' : '0.65rem', lineHeight: 1.2 }}>
                                                                    Anonymous
                                                                </Typography>
                                                    )}
                                                </Box>
                                                </Box>
                                        ) : (
                                            <Chip
                                                        label={isMobile ? '?' : 'Unknown'}
                                                size='small'
                                                variant='outlined'
                                                color='default'
                                            />
                                        )}
                                    </TableCell>

                                        {/* Activity Cell - stacked with screen info on mobile */}
                                        <TableCell sx={{
                                            px: isMobile ? 1.5 : 2,
                                            py: isMobile ? 1.5 : undefined,
                                            verticalAlign: 'middle'
                                        }}>
                                            <Box display='flex' flexDirection='column' alignItems='flex-start' gap={0.75}>
                                                <Box display='flex' alignItems='center' gap={1}>
                                                    <Chip
                                                        label={formatActivityType(activity.activityType)}
                                                        size='small'
                                                        sx={{
                                                            backgroundColor: getActivityTypeColor(activity.activityType),
                                                            color: '#FFFFFF',
                                                            fontWeight: 'bold',
                                                            fontSize: isMobile ? '0.65rem' : '0.700rem',
                                                            height: isMobile ? 22 : 24,
                                                            '& .MuiChip-label': {
                                                                px: 1,
                                                            }
                                                        }}
                                                    />
                                                    {/* Show details icon on mobile */}
                                                    {isMobile && activity.activityData && Object.keys(activity.activityData).length > 0 && (
                                                        <IconButton
                                                            size='small'
                                                            onClick={() => handleOpenDetails(activity.activityData || {}, activity.activityType)}
                                                            sx={{
                                                                p: 0.5,
                                                                color: theme.palette.text.secondary,
                                                                '&:hover': {
                                                                    color: theme.palette.primary.main,
                                                                }
                                                            }}
                                                        >
                                                            <Visibility sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                {/* Show screen name below activity on mobile */}
                                                {isMobile && activity.screenName && (
                                                    <Typography
                                                        variant='caption'
                                                        color='text.secondary'
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            lineHeight: 1.2,
                                                            pl: 0.5
                                                        }}
                                                    >
                                                        {activity.screenName}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>

                                        {/* Screen Cell - desktop/tablet only */}
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, px: 2 }}>
                                            <Typography variant='caption'>
                                                {activity.screenName || '-'}
                                            </Typography>
                                    </TableCell>

                                        {/* Device Cell - desktop only */}
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                        <Box display='flex' flexDirection='column'>
                                            {activity.deviceType && (   
                                                <Typography variant='caption'>
                                                    {activity.deviceType.charAt(0).toUpperCase() + activity.deviceType.slice(1) || '-'}
                                                </Typography>
                                            )}
                                            {activity.browser && (
                                                <Typography variant='caption' color='text.secondary'>
                                                    {activity.browser}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>

                                        {/* Details Cell - desktop only */}
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                        {activity.activityData && Object.keys(activity.activityData).length > 0 && (
                                                <IconButton
                                                    size='small'
                                                    onClick={() => handleOpenDetails(activity.activityData || {}, activity.activityType)}
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.mode === 'dark'
                                                            ? 'rgba(255, 255, 255, 0.08)'
                                                            : 'rgba(0, 0, 0, 0.04)',
                                                    }
                                                }}
                                            >
                                                    <Visibility fontSize='small' />
                                                </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component='div'
                    count={activities.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </PortalTable>

            {/* Activity Details Modal */}
            <Dialog
                open={detailsModalOpen}
                onClose={handleCloseDetails}
                maxWidth={selectedActivityType === 'product_view' ? 'xs' : 'md'}
                fullWidth={selectedActivityType !== 'product_view'}
                PaperProps={selectedActivityType === 'product_view' ? {
                    sx: {
                        borderRadius: 4,
                        overflow: 'hidden',
                        maxWidth: 280,
                        m: 2
                    }
                } : undefined}
            >
                {selectedActivityType === 'product_view' && selectedActivityData?.name ? (
                    // Render product item like in Shop
                    <Box
                        sx={{
                            backgroundColor: theme.customColors?.cardBackground || theme.palette.background.paper,
                            p: 1.5,
                        }}
                    >
                        {/* Product Image */}
                        <Box sx={{ position: 'relative', mb: 2 }}>
                            <Box
                                sx={{
                                    backgroundColor: 'white',
                                    overflow: 'hidden',
                                    borderRadius: 2.5,
                                    aspectRatio: '1/1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {selectedActivityData.image ? (
                                    <Box
                                        component='img'
                                        src={selectedActivityData.image}
                                        alt={selectedActivityData.name}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: selectedActivityData.thumbnailResizeMode || 'cover',
                                        }}
                                    />
                                ) : (
                                    <Typography
                                        sx={{
                                            fontSize: 48,
                                            color: theme.palette.text.disabled,
                                        }}
                                    >
                                        📦
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Product Details */}
                        <Box>
                            <Typography
                                variant='subtitle1'
                                sx={{
                                    fontSize: 16,
                                    fontWeight: 'bold',
                                    letterSpacing: 0.5,
                                    color: theme.palette.text.primary,
                                    mb: 0.5,
                                }}
                            >
                                {selectedActivityData.name}
                            </Typography>

                            {/* Price */}
                            {selectedActivityData.price !== undefined && (
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: theme.palette.primary.main,
                                        mb: 0.5,
                                    }}
                                >
                                    R{Number(selectedActivityData.price).toFixed(2)}
                                </Typography>
                            )}

                            {/* Rating */}
                            <Rating
                                value={5}
                                readOnly
                                size='small'
                                sx={{
                                    mt: 0.5,
                                    '& .MuiRating-iconFilled': {
                                        color: '#FFC107',
                                    },
                                }}
                            />

                            {/* Category & Code */}
                            <Box sx={{ mt: 1 }}>
                                {selectedActivityData.category && (
                                    <Typography
                                        variant='caption'
                                        sx={{
                                            display: 'block',
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        {selectedActivityData.category}
                                    </Typography>
                                )}
                                {selectedActivityData.code && (
                                    <Typography
                                        variant='caption'
                                        sx={{
                                            display: 'block',
                                            color: theme.palette.text.disabled,
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        Code: {selectedActivityData.code}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Close Button */}
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                            <BasicButton
                                title='Close'
                                onClick={handleCloseDetails}
                                variant='outlined'
                                sx={{ width: '100%' }}
                            />
                        </Box>
                    </Box>
                ) : (
                    // Default JSON view for other activity types
                    <>
                            <DialogTitle sx={{ fontWeight: 'bold' }}>
                                Activity Details
                            </DialogTitle>
                            <DialogContent dividers>
                                <Box
                                    component='pre'
                                    sx={{
                                        backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#F5F5F5',
                                        padding: 2,
                                        borderRadius: 2,
                                        overflow: 'auto',
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem',
                                        color: theme.palette.text.primary,
                                        border: `1px solid ${theme.palette.divider}`,
                                        margin: 0,
                                    }}
                                >
                                    {selectedActivityData ? JSON.stringify(selectedActivityData, null, 2) : ''}
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <BasicButton
                                    title='Close'
                                    onClick={handleCloseDetails}
                                    variant='outlined'
                                />
                            </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Linked Users Popper */}
            <Popper
                open={linkedUsersOpen}
                anchorEl={linkedUsersAnchor}
                placement='bottom-start'
                transition
                sx={{ zIndex: 1300 }}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                        <Paper
                            sx={{
                                p: 2,
                                maxWidth: 350,
                                boxShadow: theme.shadows[8],
                                border: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <Typography variant='subtitle2' fontWeight='bold' sx={{ mb: 1 }}>
                                Users Linked to this Device
                            </Typography>
                            <Typography variant='caption' color='text.secondary' sx={{ mb: 2, display: 'block' }}>
                                {selectedLinkedUsers.length} user(s) have signed in from this browser
                            </Typography>
                            <List dense>
                                {selectedLinkedUsers.map((user, index) => (
                                    <ListItem
                                        key={index}
                                        sx={{
                                            px: 0,
                                            borderBottom: index < selectedLinkedUsers.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                                        }}
                                    >
                                        <ListItemText
                                            primary={user.email}
                                            secondary={`Linked on ${new Date(user.linkedAt).toLocaleDateString()}`}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                fontWeight: 'medium'
                                            }}
                                            secondaryTypographyProps={{
                                                variant: 'caption'
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                            <BasicButton
                                title='Close'
                                onClick={handleCloseLinkedUsers}
                                sx={{ mt: 1, width: '100%' }}
                                variant='outlined'
                            />
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </Box>
    );
};

export default AuditTable;
